import type React from 'react'
import { useCallback, useRef } from 'react'
import { useActiveObject } from '@/components/app/editor/active-object-context'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { dispatchShapeCommit } from '../helpers/dispatch-shape-commit'
import type { EffectControlProps } from '../types/effect-control-props'
import type { GeometryEffect } from '../types/geometry-effect'

interface GeometryEffectDocumentOptions {
  effect: GeometryEffect
  shapeManagerRef: React.RefObject<ShapeManagerRef | null> | undefined
  /** Selected shapes this effect applies to. */
  eligible: ShapeData[]
  /** Every selected shape, eligible or not. */
  selected: ShapeData[]
  onShapeCommitted: EffectControlProps['onShapeCommitted']
  onBatchShapesCommitted: EffectControlProps['onBatchShapesCommitted']
}

/**
 * Document side of a geometry effect: the edit session's start values, the live apply while the
 * slider / preset / field changes, and the history commit that pushes one undo step.
 */
export function useGeometryEffectDocument({
  effect,
  shapeManagerRef,
  eligible,
  selected,
  onShapeCommitted,
  onBatchShapesCommitted,
}: GeometryEffectDocumentOptions) {
  const { setActiveObject, setActiveObjects } = useActiveObject()
  const sessionStartsRef = useRef<Map<string, number>>(new Map())
  /**
   * Pre-edit `kindId` per shape, captured alongside `sessionStartsRef`. The live apply stamps
   * kindId 10 on whatever it converts (Sides / Inset), so by commit time the shape can no longer
   * tell us what it was — and undo of a rectangle's first conversion has to restore kindId 1.
   */
  const sessionKindIdRef = useRef<Map<string, number | undefined>>(new Map())

  /** Record each eligible shape's start value (and kind) without resetting the session. */
  const snapshotStarts = useCallback(() => {
    const mgr = shapeManagerRef?.current

    if (effect.startSource === 'live' && !mgr) return
    const all = effect.startSource === 'live' ? (mgr?.getShapes() ?? []) : []

    for (const s of eligible) {
      const cur = effect.startSource === 'live' ? (all.find((x) => x.id === s.id) ?? s) : s

      sessionStartsRef.current.set(s.id, effect.readValue(cur))
      sessionKindIdRef.current.set(s.id, cur.kindId)
    }
  }, [effect, eligible, shapeManagerRef])

  /** Start a fresh session (slider pointer-down, preset click). */
  const beginSession = useCallback(() => {
    sessionStartsRef.current = new Map()
    sessionKindIdRef.current = new Map()
    snapshotStarts()
  }, [snapshotStarts])

  const clearStarts = useCallback(() => {
    sessionStartsRef.current.clear()
  }, [])

  const clearSession = useCallback(() => {
    sessionStartsRef.current.clear()
    sessionKindIdRef.current.clear()
  }, [])

  /**
   * Refresh the active-object mirror after a live edit. Keyed on the **whole** selection, not the
   * eligible subset: `setActiveObject` replaces the selection with one item, so calling it while
   * two objects are selected (even if only one is eligible) collapses the multi-selection.
   */
  const refreshActiveObjects = useCallback(() => {
    const mgr = shapeManagerRef?.current

    if (!mgr) return
    const all = mgr.getShapes()
    const refreshed = selected.map((s) => all.find((x) => x.id === s.id) ?? s)

    if (refreshed.length === 1) {
      setActiveObject(refreshed[0])
    } else {
      setActiveObjects(refreshed)
    }
  }, [selected, shapeManagerRef, setActiveObject, setActiveObjects])

  const applyLive = useCallback(
    (value: number) => {
      const mgr = shapeManagerRef?.current

      if (!mgr || eligible.length === 0) return
      const v = effect.normalize(value)

      for (const s of eligible) {
        const cur = mgr.getShapes().find((x) => x.id === s.id) ?? s
        const update = effect.liveUpdate(cur, v)

        if (update) {
          mgr.updateShape(s.id, update)
        }
      }

      refreshActiveObjects()
    },
    [effect, eligible, shapeManagerRef, refreshActiveObjects]
  )

  const commitWithHistory = useCallback(
    (nextValue: number) => {
      const mgr = shapeManagerRef?.current

      if (!mgr || eligible.length === 0) return
      const v = effect.normalize(nextValue)
      const items: BatchShapeTransformItem[] = []
      const all = mgr.getShapes()

      for (const s of eligible) {
        const sh = all.find((x) => x.id === s.id) ?? s
        const start =
          sessionStartsRef.current.get(s.id) ??
          effect.readValue(effect.startSource === 'live' ? sh : s)

        if (start === v) continue

        // `.has()` rather than `?? sh.kindId`: a shape whose pre-edit kind was *undefined* (freshly
        // drawn, never stamped) is a real snapshot value, and must not fall through to the
        // post-apply kind — that would report it as having started out already converted.
        const startKindId = sessionKindIdRef.current.has(s.id)
          ? sessionKindIdRef.current.get(s.id)
          : sh.kindId

        items.push({ shapeId: s.id, ...effect.historyItem(sh, start, v, startKindId) })
      }

      dispatchShapeCommit(items, mgr, onShapeCommitted, onBatchShapesCommitted)
    },
    [effect, eligible, onBatchShapesCommitted, onShapeCommitted, shapeManagerRef]
  )

  return {
    beginSession,
    snapshotStarts,
    clearStarts,
    clearSession,
    applyLive,
    commitWithHistory,
  }
}
